import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Popover } from '@creditornot/cb-popover';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Typography } from 'common/typography';

type Legend = {
  color: string;
  key: string | number;
};

export type HeatMapDatum<T extends string> = {
  x: string;
  y: string;
  key: T;
  value: string | number;
};

type HeatMapProps<T extends string> = {
  iteratorX: Iterable<string>;
  formatterX: (value: string) => string;
  iteratorY: Iterable<string>;
  formatterY: (value: string) => string;
  data: HeatMapDatum<T>[];
  formatterBlock: (value: string | number) => string;
  onResize?: (width: number) => void;
  legends: Legend[];
  translations: Record<T, string>;
};

const HeatMapContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
`;

const AxisLabel = styled(Typography.Small)`
  flex: 1 0 0;
  text-align: center;
  color: ${cbSemanticCssVars.textSecondary};
`;

const YAxisContainer = styled.div`
  display: flex;
  padding-block-start: 25px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
`;

const YAxisBlock = styled.div`
  display: flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
`;

const XAxisAndDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1;
`;

const XAxisContainer = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: flex-start;
  align-self: stretch;
  height: auto;
  padding-block: 4px;
`;

const DataContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: 2px;
`;

const DataRow = styled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
  gap: 2px;
`;

const DataBlockContainer = styled.div`
  display: flex;
  flex: 1 0 0;
  align-self: stretch;
  height: 45px;
  border-radius: 4px;
  min-width: 45px;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledPopover = styled(Popover)`
  display: flex;
  flex: 1 0 0;
`;

const DataBlock = styled.div<{ color: string; opacity: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  flex-basis: 0;
  align-self: stretch;
  height: 45px;
  border-radius: 4px;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  background-color: ${({ color, opacity }) =>
    `rgba(${color
      .match(/\d+(\.\d+)?/g)
      ?.map(Number)
      .slice(0, 3)
      .join(',')}, ${opacity})`};
  overflow: hidden;
`;

const ToolTipRoot = styled.div`
  background-color: ${cbSemanticCssVars.textPrimary};
  border-radius: 12px;
  color: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  flex-direction: column;
  padding: 12px;
  gap: 16px;
  min-width: 194px;
`;

const TooltipRowRoot = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

const TooltipRow = ({ content }: { content: string }) => (
  <TooltipRowRoot>
    <Typography.Small>{content}</Typography.Small>
  </TooltipRowRoot>
);

export const HeatMap = <T extends string>({
  iteratorX,
  formatterX,
  iteratorY,
  formatterY,
  data,
  legends,
  formatterBlock,
  onResize,
  translations,
}: HeatMapProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const handleResize = useCallback(() => {
    ref.current && onResize && onResize(ref.current?.offsetWidth ?? 0);
  }, [ref, onResize]);

  useEffect(() => {
    handleResize();
    setVisible(true);
  }, [handleResize]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [ref, handleResize]);

  const yAxis = [...iteratorY];
  const xAxis = [...iteratorX];

  const dataMap = new Map<string, Map<string, HeatMapDatum<T>[]>>();
  data.forEach(datum => {
    const xMap = dataMap.get(datum.x) ?? new Map<string, HeatMapDatum<T>[]>();
    const yData = xMap.get(datum.y) ?? [];
    yData.push(datum);
    xMap.set(datum.y, yData);
    dataMap.set(datum.x, xMap);
  });

  // calculate minimum y domain range which has data
  // todo @navjotahuja92: move this out of heatmap
  const minY = Math.min(...data.map(datum => Number(datum.y)));
  const finalMinY = Number.isFinite(minY) ? minY : 9;
  const maxY = Math.max(...data.map(datum => Number(datum.y)));
  const finalMaxY = Number.isFinite(maxY) ? maxY : 14;
  const yAxisRange = yAxis.filter(y => finalMinY <= Number(y) && Number(y) <= finalMaxY);

  return (
    <HeatMapContainer ref={ref}>
      <div id="portalElement"></div>
      <YAxisContainer>
        {yAxisRange.map(yKey => {
          return (
            <YAxisBlock key={yKey}>
              <AxisLabel>{formatterY(yKey)}</AxisLabel>
            </YAxisBlock>
          );
        })}
      </YAxisContainer>
      {visible && (
        <XAxisAndDataContainer>
          <XAxisContainer>
            {xAxis.map(xKey => {
              return <AxisLabel key={xKey}>{formatterX(xKey)}</AxisLabel>;
            })}
          </XAxisContainer>
          <DataContainer>
            {yAxisRange.map(yKey => {
              return (
                <DataRow key={yKey}>
                  {xAxis.map(xKey => {
                    const targetData = dataMap.get(xKey)?.get(yKey) ?? [];

                    const blocks = targetData.map(datum => {
                      const color = legends.find(legend => legend.key === datum?.key)?.color;
                      return {
                        key: datum.key,
                        value: datum.value,
                        color: color ?? 'transparent',
                      };
                    });

                    return (
                      <DataBlockContainer key={`${xKey}-${yKey}`}>
                        {blocks.length > 0 && (
                          <StyledPopover
                            disableContentPadding
                            renderArrow={false}
                            portalElement={document.getElementById('portal')}
                            showOnClick={false}
                            content={
                              <ToolTipRoot>
                                <TooltipRow content={`${xKey} - ${formatterY(yKey)}`} />
                                {blocks.map(({ key, value }) => (
                                  <TooltipRow key={key} content={`${translations[key]} ${formatterBlock(value)}`} />
                                ))}
                              </ToolTipRoot>
                            }
                          >
                            {blocks.map(({ value, color }) => (
                              // todo @navjotahuja92: move this logic out of heatmap
                              <DataBlock
                                key={`${xKey}-${yKey}-${color}`}
                                color={color}
                                opacity={0.2 + (Number(value) / 60) * 0.6}
                              >
                                <Typography.Tiny>{formatterBlock(value)}</Typography.Tiny>
                              </DataBlock>
                            ))}
                          </StyledPopover>
                        )}
                      </DataBlockContainer>
                    );
                  })}
                </DataRow>
              );
            })}
          </DataContainer>
        </XAxisAndDataContainer>
      )}
    </HeatMapContainer>
  );
};
